import type {
	FilterRuleModel,
	PricingCampaignModel,
	PricingCampaignSettingsModel,
	SettingsTemplateModel,
} from '@/shared/generated'
import type { ResolverResult, UseFormReturn } from 'react-hook-form'

import React, { createContext, use, useEffect, useMemo } from 'react'
import { toNestErrors } from '@hookform/resolvers'
import { AxiosError } from 'axios'
import { addDays, format } from 'date-fns'
import { omitAll, propOr, sum } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useCheckOptimizationForDeleteExport,
	useOptimizationIsReadyForChange,
} from '@/entities/optimization'
import { useWithAffectedOGsHandler } from '@/entities/optimization-group'
import { cretePricingCampaignSchemaAsync } from '@/entities/pricing-campaign'
import { formatLegacyTactics } from '@/entities/pricing-rule'

import { PricingEngineType, TargetPriceTypeModel } from '@/shared/generated'
import { ajvResolver } from '@/shared/lib/validator/ajv'
import { parseZod4Issues } from '@/shared/lib/validator/zod'
import { DateFormat } from '@/shared/tools/dates'
import { getSchemaDefaultState } from '@/shared/tools/json-schema-utils'

import { usePricingCampaignData } from '../lib/usePricingCampaignData'
import { usePricingCampaignMutations } from '../lib/usePricingCampaignMutations'

type PricingCampaignContextType = {
	isReadOnly?: boolean
	draft?: {
		name?: string
		engine?: string
		assignment_filters?: FilterRuleModel[]
	}
	isNew: boolean
	isGlobal: boolean
	isMainGlobal: boolean
	isSimulatedGlobal: boolean
	isReadyToChange: boolean
	pricingCampaignId?: string
	optimizationGroupId?: string | null
	scenarioId?: string
	isScenarioContext: boolean
	isGlobalSetContext: boolean
	methods: UseFormReturn<PricingCampaignModel, any, PricingCampaignModel>
	pricingCampaign?: PricingCampaignModel
	productsCount: number
	isLoading: boolean
	isPending: boolean
	isSubmitting: boolean
	handleSubmit: () => void
	handleSubmitAndUpdate: () => void
	handleReset: () => void
	handleUseTemplate: (template: SettingsTemplateModel) => void
}
export const PricingCampaignContext = createContext<PricingCampaignContextType>(
	{
		isGlobal: false,
		isMainGlobal: false,
		isSimulatedGlobal: false,
		isLoading: false,
		isPending: false,
		isReadyToChange: false,
		isSubmitting: false,
		isNew: true,
		pricingCampaignId: '',
		isScenarioContext: false,
		isGlobalSetContext: false,
		methods: {} as UseFormReturn<PricingCampaignModel>,
		handleSubmit: () => {},
		handleSubmitAndUpdate: () => {},
		handleReset: () => {},
		productsCount: 0,
		handleUseTemplate: () => {},
	}
)
export const PricingCampaignProvider = ({
	isReadOnly,
	globalSetId,
	pricingCampaign,
	optimizationGroupId,
	scenarioId,
	draft,
	children,
}: {
	isReadOnly?: boolean
	globalSetId?: string
	pricingCampaign?: PricingCampaignModel
	children: React.ReactNode
	optimizationGroupId?: string | null
	scenarioId?: string | null

	draft?: {
		name?: string
		engine?: string
		assignment_filters?: FilterRuleModel[]
	}
}) => {
	const { t } = useTranslation()

	const checkExport = useCheckOptimizationForDeleteExport(optimizationGroupId)
	const pricingCampaignId = pricingCampaign?.id
	const {
		mainScenarioId,
		isMainGlobalPcSet,
		schema,
		productsCountbyPc,
		filtersProductsCount,
		isLoading,
	} = usePricingCampaignData({
		optimizationGroupId,
		globalSetId,
		pricingCampaignId,
		draft,
	})
	const isScenarioContext = Boolean(
		!!scenarioId && scenarioId !== mainScenarioId && optimizationGroupId
	)
	const isGlobal = !!globalSetId
	const isMainGlobal = isGlobal && !!isMainGlobalPcSet
	const {
		assignProducts,
		createPricingCampaignMutation: baseCreateMutation,
		updatePricingCampaignMutation: baseUpdateMutation,
		commitMutation,
		isPending,
	} = usePricingCampaignMutations({ optimizationGroupId, isScenarioContext })
	const createWith424Handler = useWithAffectedOGsHandler(baseCreateMutation)
	const updateWith424Handler = useWithAffectedOGsHandler(baseUpdateMutation)
	const createPricingCampaignMutation = isMainGlobal
		? createWith424Handler
		: baseCreateMutation
	const updatePricingCampaignMutation = isMainGlobal
		? updateWith424Handler
		: baseUpdateMutation
	const isNew = !pricingCampaignId
	useEffect(() => {
		const defaultTargetPriceType = TargetPriceTypeModel.Auto
		if (!isNew && pricingCampaign) {
			const target_price_type =
				pricingCampaign.settings?.pricing_tactics?.target_price_type

			methods.reset({
				name: pricingCampaign?.name,
				settings: {
					...pricingCampaign.settings,
					pricing_tactics: {
						...formatLegacyTactics(pricingCampaign.settings?.pricing_tactics),
						target_price_type: target_price_type || defaultTargetPriceType,
					},
				},
				id: pricingCampaign.id,
			})
		}

		if (isNew && schema) {
			const defaultValues = getSchemaDefaultState(schema)
			if (draft?.engine) {
				const pricing_tactics = {
					[PricingEngineType.RB]: {
						engine: PricingEngineType.RB,
						params: {
							pricing_rule_id: null,
							pricing_rule_version_id: null,
							rule_params: {},
						},
						target_price_type: defaultTargetPriceType,
					},
					[PricingEngineType.ML]: {
						engine: PricingEngineType.ML,
						params: {},
						target_price_type: defaultTargetPriceType,
					},
					[PricingEngineType.MD]: {
						engine: PricingEngineType.MD,
						params: {
							periods: [],
						},
						target_price_type: defaultTargetPriceType,
					},
					[PricingEngineType.Promo]: {
						engine: PricingEngineType.Promo,
						params: {
							promo_start_date: format(new Date(), DateFormat.system),
							promo_end_date: format(
								addDays(new Date(), 13),
								DateFormat.system
							),
							target: {
								name: 'sales_items_uplift',
								value: 0,
							},
						},
					},
				}[draft?.engine || PricingEngineType.ML]
				defaultValues.settings = {
					...(defaultValues.settings || {}),
					pricing_tactics,
				}
			}

			methods.reset({
				name: draft?.name || '',
				...defaultValues,
			})
		}
	}, [pricingCampaign, schema, isNew])
	const isSimulatedGlobal = isGlobal && !isMainGlobal
	const isLocal = !isGlobal && !!optimizationGroupId
	const isGlobalSetContext = isGlobal && !isMainGlobal
	const optimizationIsReadyForChange =
		useOptimizationIsReadyForChange(optimizationGroupId)
	const isReadyToChange = isLocal ? optimizationIsReadyForChange : !isMainGlobal
	const methods = useForm<PricingCampaignModel>({
		async resolver(values, context, options) {
			const validateAjv = ajvResolver(schema!, {
				strict: false,
			})

			/**
			 * Use Zod for validation for smoke validation
			 */
			const { error, data } =
				await cretePricingCampaignSchemaAsync.safeParseAsync(values)
			if (error) {
				return {
					values: {} as Record<string, never>,
					errors: toNestErrors(
						parseZod4Issues(
							error.issues,
							!options.shouldUseNativeValidation &&
								options.criteriaMode === 'all'
						),
						options
					),
				} satisfies ResolverResult<PricingCampaignModel>
			}

			/**
			 * Use Ajv for validation for full validation
			 */
			return validateAjv(
				{
					...values,
					settings: {
						...values.settings,
						// TODO: Fix this type after the API is updated
						pricing_tactics: data.settings?.pricing_tactics as any,
					},
				},
				context,
				options
			)
		},
	})
	const createOrUpdatePricingCampaign = async (data: PricingCampaignModel) => {
		/**
		 * For main global campaigns, the 424 handler automatically intercepts
		 * and handles affected optimization groups via modal.
		 */
		if (isGlobal) {
			if (isNew) {
				return await createPricingCampaignMutation.mutateAsync({
					...omitAll(['id'], data),
					optimization_group_id: null,
					scenario_id: null,
					global_pc_set_id: globalSetId,
				})
			} else {
				return await updatePricingCampaignMutation.mutateAsync(data)
			}
		} else {
			if (isNew) {
				return await createPricingCampaignMutation.mutateAsync({
					...omitAll(['id'], data),
					optimization_group_id: optimizationGroupId,
					scenario_id: scenarioId || mainScenarioId,
				})
			} else {
				return await updatePricingCampaignMutation.mutateAsync(data)
			}
		}
	}
	const upsertPricingCampaignAndAssign = async (data: PricingCampaignModel) => {
		const pricingCampaign = await createOrUpdatePricingCampaign(
			omitAll(['optimization_group_id'], data)
		)
		if (pricingCampaign && draft?.assignment_filters?.length) {
			await assignProducts.mutateAsync({
				pricingCampaignId: pricingCampaign.id,
				data: {
					filters: draft?.assignment_filters,
				},
			})
		}
	}
	const handleUseTemplate = ({ body }: SettingsTemplateModel) => {
		const settings = body[
			'settings' as keyof typeof body
		] as PricingCampaignSettingsModel
		if (settings) {
			/**
			 * 	Hack bacause of the useFiealdArray subscribed to the changes of the array not fot the values
			 *	https://github.com/react-hook-form/react-hook-form/blob/master/src/useFieldArray.ts#L122
			 * 	Issue [PP-1314]
			 */
			for (const _key in settings) {
				const key = _key as keyof PricingCampaignSettingsModel
				methods.setValue(
					`settings.${key}`,
					settings[key] as PricingCampaignSettingsModel[typeof key],
					{
						shouldDirty: true,
						shouldValidate: true,
					}
				)
			}
		}
	}
	const _handleSubmit = async (data: PricingCampaignModel) => {
		try {
			await upsertPricingCampaignAndAssign(data)
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error.status === 424) return

				toast.error(
					`${t('toast.entity.failed', { entity: t('entity.pricing_campaign') })} "${error.response?.data.detail}"`,
					{
						duration: 5000,
						id: 'pricing-campaign',
					}
				)
			} else {
				toast.error(t('fatal_error_title'), {
					duration: 5000,
					id: 'pricing-campaign',
				})
			}
		}
	}
	const _handleSubmitAndUpdate = async (data: PricingCampaignModel) => {
		try {
			await upsertPricingCampaignAndAssign(data)
			if (optimizationGroupId) {
				await commitMutation.mutateAsync(optimizationGroupId)
			}
		} catch (error) {
			toast.error(t('fatal_error_title'), {
				duration: 5000,
				id: 'pricing-campaign',
			})
		}
	}
	const productsCount = useMemo(() => {
		if (isNew) {
			return filtersProductsCount || 0
		} else {
			return sum((productsCountbyPc || []).map(propOr(0, 'product_count'))) || 0
		}
	}, [productsCountbyPc, filtersProductsCount, isNew])
	const contextValue = useMemo(
		() => ({
			draft,
			isNew,
			isLoading,
			isPending,
			isGlobal,
			isMainGlobal,
			isSimulatedGlobal,
			pricingCampaign,
			isReadyToChange:
				isMainGlobal || isScenarioContext || Boolean(isReadyToChange),
			pricingCampaignId,
			optimizationGroupId,
			scenarioId: scenarioId || mainScenarioId,
			isScenarioContext,
			isGlobalSetContext,
			methods,
			productsCount,
			isSubmitting: methods.formState.isSubmitting,
			isReadOnly,
			handleSubmit: methods.handleSubmit(
				isScenarioContext ? _handleSubmit : checkExport(_handleSubmit)
			),
			handleSubmitAndUpdate: methods.handleSubmit(
				isScenarioContext
					? _handleSubmitAndUpdate
					: checkExport(_handleSubmitAndUpdate)
			),
			handleReset: () => methods.reset(),
			handleUseTemplate,
		}),
		[
			isReadOnly,
			draft,
			isNew,
			isLoading,
			isPending,
			isMainGlobal,
			isReadyToChange,
			pricingCampaignId,
			pricingCampaign,
			optimizationGroupId,
			scenarioId,
			mainScenarioId,
			isScenarioContext,
			methods,
			isGlobalSetContext,
			_handleSubmit,
			checkExport,
			_handleSubmitAndUpdate,
			handleUseTemplate,
			productsCount,
			isGlobal,
			isSimulatedGlobal,
		]
	)

	return (
		<PricingCampaignContext value={contextValue}>
			{children}
		</PricingCampaignContext>
	)
}

export const usePricingCampaignContext = () => use(PricingCampaignContext)
